import { z } from 'zod';
import { IdSchema, MoneySchema, TimestampSchema, ListQuerySchema } from './common.js';

export const TimeBandSchema = z.enum(['anytime', 'peak', 'offpeak', 'weekend', 'holiday']);
export const DeckTypeSchema = z.enum(['cost', 'sell']);

export const RateDeckSchema = z.object({
  id: IdSchema,
  owner_type: z.enum(['platform', 'carrier', 'reseller', 'tenant']),
  owner_id: IdSchema.nullable(),
  name: z.string(),
  deck_type: DeckTypeSchema,
  currency: z.string().length(3),
  published_version: z.number().int().nullable(),
  rule_count: z.number().int().optional(),
  created_at: TimestampSchema,
});
export type RateDeck = z.infer<typeof RateDeckSchema>;

export const RateRuleSchema = z.object({
  id: IdSchema,
  prefix: z.string(),
  description: z.string().nullable(),
  time_band: TimeBandSchema,
  rate_per_minute: MoneySchema,
  connection_fee: MoneySchema,
  minimum_charge: MoneySchema,
  free_seconds: z.number().int(),
  first_increment_sec: z.number().int(),
  subsequent_increment_sec: z.number().int(),
});
export type RateRule = z.infer<typeof RateRuleSchema>;

export const RateDeckCreateSchema = z.object({
  name: z.string().min(1).max(160),
  deck_type: DeckTypeSchema,
  owner_type: z.enum(['platform', 'carrier', 'reseller', 'tenant']).default('platform'),
  owner_id: IdSchema.optional(),
  currency: z.string().length(3).default('ZAR'),
}).strict();

export const RateRuleInputSchema = z.object({
  prefix: z.string().regex(/^\d{1,15}$/, 'digits only, E.164 without the plus'),
  description: z.string().max(200).optional(),
  time_band: TimeBandSchema.default('anytime'),
  rate_per_minute: MoneySchema,
  connection_fee: MoneySchema.default('0'),
  minimum_charge: MoneySchema.default('0'),
  free_seconds: z.number().int().min(0).max(3600).default(0),
  /** Per second unless a rule says otherwise (owner, 17 September 2026). */
  first_increment_sec: z.number().int().min(1).max(3600).default(1),
  subsequent_increment_sec: z.number().int().min(1).max(3600).default(1),
}).strict();

export const RateRuleBulkSchema = z.object({
  rules: z.array(RateRuleInputSchema).min(1).max(5000),
  /** Replace every rule in the draft version rather than merging. */
  replace: z.boolean().default(false),
}).strict();

/**
 * The rate lookup tester (doc 07 section 4.10).
 *
 * "Small and disproportionately valuable" -- it is how a billing dispute is
 * settled in one minute instead of one afternoon, because it shows exactly
 * which rule matched and every step of the arithmetic.
 */
export const RateTestSchema = z.object({
  destination: z.string().min(1).max(24),
  duration_sec: z.number().int().min(0).max(86400).default(60),
  answered: z.boolean().default(true),
  band: TimeBandSchema.optional(),
}).strict();

export const RateTestResultSchema = z.object({
  ok: z.boolean(),
  charge: MoneySchema.nullable(),
  currency: z.string().length(3).nullable(),
  billable_seconds: z.number().int().nullable(),
  reason: z.string().nullable(),
  breakdown: z.object({
    matched_prefix: z.string(),
    rule_id: IdSchema,
    band: TimeBandSchema,
    raw_seconds: z.number().int(),
    free_seconds: z.number().int(),
    billable_seconds: z.number().int(),
    rate_per_minute: MoneySchema,
    connection_fee: MoneySchema,
    minimum_charge_applied: z.boolean(),
  }).nullable(),
});

export const BillingAccountSchema = z.object({
  id: IdSchema,
  tenant_id: IdSchema.nullable(),
  reseller_id: IdSchema.nullable(),
  mode: z.enum(['prepaid', 'postpaid']),
  currency: z.string().length(3),
  credit_limit: MoneySchema,
  low_balance_threshold: MoneySchema.nullable(),
  billing_day: z.number().int(),
  vat_rate: MoneySchema,
  status: z.enum(['active', 'suspended', 'closed']),
  balance: MoneySchema,
  reserved: MoneySchema,
  available: MoneySchema,
  /** An invoice 21 days overdue suspends the account (docs/28 B3). */
  auto_suspend: z.boolean(),
  /** Suspended now for an overdue invoice, which paying lifts. */
  suspended_for_overdue: z.boolean(),
});
export type BillingAccount = z.infer<typeof BillingAccountSchema>;

export const BillingAccountUpsertSchema = z.object({
  mode: z.enum(['prepaid', 'postpaid']),
  currency: z.string().length(3).default('ZAR'),
  credit_limit: MoneySchema.default('0'),
  low_balance_threshold: MoneySchema.optional(),
  billing_day: z.number().int().min(1).max(28).default(1),
  vat_rate: MoneySchema.default('0.15'),
  sell_rate_deck_id: IdSchema.optional(),
}).strict();

export const MovementSchema = z.object({
  id: IdSchema,
  kind: z.enum(['call_charge', 'topup', 'adjustment', 'invoice', 'refund', 'monthly_fee', 'reversal']),
  amount: MoneySchema,
  currency: z.string().length(3),
  cdr_id: IdSchema.nullable(),
  description: z.string().nullable(),
  created_at: TimestampSchema,
});
export type Movement = z.infer<typeof MovementSchema>;

export const MovementListQuerySchema = ListQuerySchema.extend({
  kind: z.string().max(32).optional(),
});

/**
 * A manual balance adjustment. `reason` is mandatory and stored: an unexplained
 * movement on a customer's ledger is unanswerable months later, and this is a
 * step-up gated action precisely because it moves money.
 */
export const AdjustmentSchema = z.object({
  amount: MoneySchema,
  kind: z.enum(['topup', 'adjustment', 'refund']).default('topup'),
  reason: z.string().min(3).max(500),
}).strict();
